// HumanInterviewPanel — the view during / after a human interview.
// Route: /jobs/:jobId/human-interview/:stageId/panel/:resumeId
//
// Laid out as the other EquaOS apps are: a header (back, the Meet link and
// the decision), then two columns of cards. CV summary (left) | Questionnaire
// answers, your marks, panel marks, and (for HR) the panel list and the
// decision (right).
//
// @story HIR-TC1 — each panellist marks on their own scorecard. Until they
// submit, the API sends them nobody else's marks; this screen just shows what
// it is given.
// @story HIR-TC2 — the candidate's earlier rounds (marks and remarks) are
// shown above, per the school's panel-view setting.
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import { Button, Card, EmptyState, Icon, Input, Select, StatusPill } from "../ds";
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from "../components/page";
import PaperMarksForm from "./PaperMarksForm";
import RubricMark, { blankRow, markTotal } from "./RubricMark";
import EarlierRounds from "./EarlierRounds";
import RouteCard from "./rounds/RouteCard";
const DECISION_CONFIG = {
  hire:   { label: "Hire",   variant: "primary", icon: "check-circle",   tone: "success" },
  hold:   { label: "Hold",   variant: "primary", icon: "warning-circle", tone: "warning" },
  reject: { label: "Reject", variant: "danger",  icon: "x",              tone: "danger"  },
};
// Small layout pieces, drawn only with the design system's variables.
const muted = { color: "var(--text-muted)", fontSize: "var(--fs-caption)" };
const soft = { background: "var(--surface-2)", borderRadius: "var(--radius-sm)", padding: "8px 10px" };
const stack = (gap = 12) => ({ display: "grid", gap });
const row = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 };
const tag = { fontSize: "var(--fs-caption)", background: "var(--surface-2)", color: "var(--text-2)", padding: "4px 8px", borderRadius: "var(--radius-sm)" };
// The Meet link is a link, drawn like the design system's secondary button.
const linkButton = {
  display: "inline-flex", alignItems: "center", gap: 8, height: 40, padding: "0 18px",
  borderRadius: "var(--radius-pill)", border: "1px solid var(--border-strong)", background: "var(--surface-1)",
  color: "var(--text-1)", fontWeight: 600, fontSize: "var(--fs-body-md)", textDecoration: "none", whiteSpace: "nowrap",
};
// @stories HIR-TC1, HIR-HD1
const fmtWhen = (d) => {
  if (!d) return "";
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? "" : t.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};
/* Earlier versions of one panellist's marks, oldest first: what they were,
   and who replaced them, when and why. Nothing is edited away. */
function RevisionList({ revisions }) {
  return (
    <ul style={{ ...stack(4), listStyle: "none", margin: 0, padding: 0 }}>
      {revisions.map((r, i) => (
        <li key={i} style={{ ...soft, ...muted }}>
          <span style={{ fontWeight: 500, color: "var(--text-2)" }}>{r.score ?? "—"}%</span>
          {(r.scorecard || []).length > 0 && ` (${r.scorecard.map((c) => `${c.label} ${c.score}/${c.maxMarks}`).join(" · ")})`}
          {" — "}replaced {fmtWhen(r.replacedAt)}{r.replacedBy?.name ? ` by ${r.replacedBy.name}` : ""}.
          {r.reason && <span style={{ display: "block", color: "var(--text-2)" }}>Reason: {r.reason}</span>}
        </li>
      ))}
    </ul>
  );
}
export default function HumanInterviewPanel() {
  const { jobId, stageId, resumeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [data, setData]         = useState(null);
  const [forbidden, setForbidden] = useState("");
  const [notes, setNotes]       = useState("");
  const [decision, setDecision] = useState(null); // 'hire'|'hold'|'reject'|null
  const [saved, setSaved]       = useState(false);
  // The signed-in person's own marks: [{ label, maxMarks, score }], or one overall mark.
  const [myCard, setMyCard]     = useState([]);
  const [myOverall, setMyOverall] = useState("");
  const [myNotes, setMyNotes]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Correcting my own submitted marks (until HR records the decision).
  const [correcting, setCorrecting] = useState(false);
  const [reason, setReason]     = useState("");
  const [people, setPeople]     = useState([]);
  const [addWho, setAddWho]     = useState("");
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/human-interview/${jobId}/${stageId}/panel/${resumeId}`);
      const d = r.data?.data;
      setData(d);
      setNotes(d?.interviewStage?.hrNotes || "");
      setDecision(d?.interviewStage?.hrDecision || null);
      if (d?.interviewStage?.hrDecision) setSaved(true);
      // My scorecard rows come from what the stage assesses.
      const crit = d?.interviewStage?.criteria || [];
      const mine = d?.myAssessment;
      setMyCard(crit.map((c) => blankRow(c, (mine?.scorecard || []).find((p) => p.label === c.label))));
      setMyNotes(mine?.notes || "");
      setMyOverall(mine && !crit.length && typeof mine.score === "number" ? String(mine.score) : "");
      setCorrecting(false);
      setReason("");
      if (d?.viewer?.isHr) {
        api.get("/human-interview/panel-people").then((p) => setPeople(p.data?.data || [])).catch(() => {});
      }
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(e.response.data?.message || "You are not on this candidate's interview panel.");
      else toast.error("Could not load interview panel");
    } finally {
      setLoading(false);
    }
  }, [jobId, stageId, resumeId]);
  useEffect(() => { load(); }, [load]);
  const saveDecision = async () => {
    if (!decision) { toast.warn("Please select a decision before saving."); return; }
    setSaving(true);
    try {
      // Marks now come from each panel member's own scorecard, so the
      // decision carries the decision and notes only.
      await api.post(`/human-interview/${jobId}/${stageId}/decision/${resumeId}`, { decision, notes });
      toast.success("Decision saved");
      setSaved(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save decision");
    } finally {
      setSaving(false);
    }
  };
  const submitMine = async () => {
    const hasCrit = myCard.length > 0;
    if (!hasCrit && myOverall === "") { toast.warn("Give a mark before submitting."); return; }
    if (hasCrit && !markTotal(myCard).complete) { toast.warn("Give every criterion a level from 1 to 4 before submitting."); return; }
    if (!window.confirm("Submit your marks? You will then see the rest of the panel's marks. You can correct your marks afterwards, with a reason, until HR records the decision.")) return;
    setSubmitting(true);
    try {
      await api.post(`/human-interview/${jobId}/${stageId}/assessment/${resumeId}`, hasCrit
        ? { scores: myCard.map(({ label, score }) => ({ label, score })), notes: myNotes }
        : { overall: Number(myOverall), notes: myNotes });
      toast.success("Your marks are submitted");
      setLoading(true);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not submit your marks");
    } finally {
      setSubmitting(false);
    }
  };
  const saveCorrection = async () => {
    const hasCrit = myCard.length > 0;
    if (!hasCrit && myOverall === "") { toast.warn("Give a mark before submitting."); return; }
    if (reason.trim().length < 3) { toast.warn("Say briefly why you are correcting your marks."); return; }
    setSubmitting(true);
    try {
      const marks = hasCrit
        ? { scores: myCard.map(({ label, score }) => ({ label, score })) }
        : { overall: Number(myOverall) };
      await api.post(`/human-interview/${jobId}/${stageId}/assessment/${resumeId}/correction`, { ...marks, notes: myNotes, reason: reason.trim() });
      toast.success("Your corrected marks are saved. Your earlier marks are kept on record.");
      setLoading(true);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save your correction");
    } finally {
      setSubmitting(false);
    }
  };
  // Cancelling puts the marks back as they are on record.
  const cancelCorrection = () => { setLoading(true); load(); };
  const addMember = async () => {
    if (!addWho) return;
    try {
      await api.post(`/human-interview/${jobId}/${stageId}/panel/${resumeId}/members`, { userId: addWho });
      setAddWho("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not add them to the panel");
    }
  };
  const removeMember = async (userId) => {
    try {
      await api.delete(`/human-interview/${jobId}/${stageId}/panel/${resumeId}/members/${userId}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not take them off the panel");
    }
  };
  if (!loading && forbidden) return (
    <Card>
      <EmptyState
        icon={<Icon name="lock-key" size={36} />}
        title="Interview panel"
        description={forbidden}
        action={<div><Button variant="secondary" size="sm" onClick={() => navigate(-1)}>Go back</Button></div>}
      />
    </Card>
  );
  if (loading) return <Card><Loading label="Loading the interview panel" /></Card>;
  const { cvSummary: cv, questionnaireData, interviewStage } = data || {};
  return (
    <Page>
      <PageHeader
        title="Interview Panel"
        subtitle={`${data?.jobTitle || ""} · ${cv?.name || ""}`}
        actions={(
          <>
            <Button variant="ghost" icon={<Icon name="arrow-left" />} onClick={() => navigate(-1)}>Back</Button>
            {interviewStage?.meetLink && (
              <a href={interviewStage.meetLink} target="_blank" rel="noreferrer" style={linkButton}>
                <Icon name="desktop" /> Open Google Meet
              </a>
            )}
            {saved && decision && (
              <span style={{ alignSelf: "center" }}>
                <StatusPill tone={DECISION_CONFIG[decision]?.tone}>Decision: {DECISION_CONFIG[decision]?.label}</StatusPill>
              </span>
            )}
          </>
        )}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))", gap: 20, alignItems: "start" }}>
        {/* LEFT — CV Summary */}
        <div style={stack(20)}>
          {/* Identity */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--brand-primary-soft)", color: "var(--brand-primary-ink)", display: "grid", placeItems: "center", flex: "none" }}>
                <Icon name="identification-badge" size={24} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: "var(--fs-display-sm)", fontWeight: 600, color: "var(--text-1)" }}>{cv?.name}</h2>
                <div style={{ color: "var(--text-2)", fontSize: "var(--fs-body-sm)" }}>{cv?.email}</div>
                {cv?.phone && <div style={{ color: "var(--text-2)", fontSize: "var(--fs-body-sm)" }}>{cv?.phone}</div>}
              </div>
            </div>
            {cv?.summary && (
              <p style={{ margin: "16px 0 0", paddingTop: 16, borderTop: "1px solid var(--border)", color: "var(--text-2)", fontSize: "var(--fs-body-sm)", lineHeight: "var(--lh-body)" }}>{cv.summary}</p>
            )}
          </Card>
          {(cv?.atsScore !== undefined || cv?.jdMatch !== undefined) && (
            <StatRow>
              {cv?.atsScore !== undefined && <Stat label="ATS" value={`${cv.atsScore}%`} />}
              {cv?.jdMatch !== undefined && <Stat label="JD Match" value={`${cv.jdMatch}%`} />}
            </StatRow>
          )}
          {/* Skills */}
          {cv?.skills?.length > 0 && (
            <Section title="Skills">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {cv.skills.slice(0, 20).map((s, i) => (
                  <span key={i} style={tag}>{s}</span>
                ))}
              </div>
            </Section>
          )}
          {/* Experience */}
          {cv?.experience?.length > 0 && (
            <Section title="Experience">
              <div style={stack(12)}>
                {cv.experience.slice(0, 4).map((exp, i) => (
                  <div key={i} style={{ fontSize: "var(--fs-body-sm)" }}>
                    <div style={{ fontWeight: 500, color: "var(--text-1)" }}>{exp.title} {exp.company ? `@ ${exp.company}` : ""}</div>
                    {(exp.startDate || exp.endDate) && (
                      <div style={muted}>{exp.startDate} – {exp.endDate || "Present"}</div>
                    )}
                    {exp.description && (
                      <div style={{ ...muted, color: "var(--text-2)", marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{exp.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}
          {/* Education */}
          {cv?.education?.length > 0 && (
            <Section title="Education">
              <div style={stack(8)}>
                {cv.education.slice(0, 3).map((edu, i) => (
                  <div key={i} style={{ fontSize: "var(--fs-body-sm)" }}>
                    <div style={{ fontWeight: 500, color: "var(--text-1)" }}>{edu.degree || edu.institution}</div>
                    {edu.degree && edu.institution && <div style={{ ...muted, color: "var(--text-2)" }}>{edu.institution}</div>}
                    {edu.year && <div style={muted}>{edu.year}</div>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
        {/* RIGHT — Questionnaire + Notes */}
        <div style={stack(20)}>
          {/* The candidate's earlier rounds (HIR-TC2) */}
          <EarlierRounds data={data?.earlierRounds} />
          {/* The whole route, opened on this round (HIR-HR34); no name for a panellist */}
          <RouteCard resumeId={resumeId} stageId={stageId} />
          {/* Questionnaire answers */}
          {questionnaireData?.length > 0 ? questionnaireData.map((qd, qi) => (
            <Section key={qi} title={qd.stageLabel} subtitle={`Score: ${qd.score ?? "—"}% (passing: ${qd.passingScore}%)`}>
              <div style={stack(12)}>
                {qd.answers.map((a, ai) => (
                  <div key={ai} style={{ paddingBottom: ai < qd.answers.length - 1 ? 12 : 0, borderBottom: ai < qd.answers.length - 1 ? "1px solid var(--border)" : "none" }}>
                    <div style={{ fontSize: "var(--fs-body-sm)", fontWeight: 500, color: "var(--text-2)", marginBottom: 4 }}>Q{ai + 1}. {a.question}</div>
                    {a.questionType === "mcq" ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--fs-body-sm)", color: "var(--text-1)" }}>
                        Selected option {a.selectedOption !== null ? a.selectedOption + 1 : "—"}
                        <StatusPill tone={a.score > 0 ? "success" : "danger"}>{a.score > 0 ? "Correct" : "Incorrect"}</StatusPill>
                      </div>
                    ) : (
                      <div style={{ ...soft, fontSize: "var(--fs-body-sm)", color: "var(--text-1)", lineHeight: "var(--lh-body)" }}>
                        {a.writtenAnswer || <span style={{ fontStyle: "italic", color: "var(--text-muted)" }}>No answer</span>}
                      </div>
                    )}
                    <div style={{ ...muted, marginTop: 4 }}>{a.score}/{a.maxScore} pts</div>
                  </div>
                ))}
              </div>
            </Section>
          )) : (
            <Section title="Questionnaire">
              <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>No questionnaire responses yet.</div>
            </Section>
          )}
          {/* @story HIR-TC1 */}
          {(() => {
            const viewer = data?.viewer || {};
            const mine = data?.myAssessment;
            const pass = interviewStage?.passingScore ?? 60;
            // The same arithmetic as the server's (RubricMark.markTotal): marks
            // out of the most, or the group's 1–4 levels weighted (HIR-TC4).
            const { earned, max, pct, levels } = markTotal(myCard);
            const locked = viewer.submitted && !correcting;
            return (
              <>
                {data?.slot?.startsAt && (
                  <Card padding="14px 20px">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "var(--fs-body-sm)", color: "var(--text-2)" }}>
                      <Icon name="calendar" />
                      Interview: <strong style={{ color: "var(--text-1)" }}>{fmtWhen(data.slot.startsAt)}</strong>
                      {data.slot.location && <span style={{ color: "var(--text-muted)" }}>· {data.slot.location}</span>}
                    </div>
                  </Card>
                )}

                {/* Your marks — only for someone on this candidate's panel */}
                {viewer.onPanel && (
                  <Section title="Your marks">
                    <div style={stack(12)}>
                      {correcting ? (
                        <Note tone="warning">
                          Correcting your marks. Your earlier marks stay on record with your reason, and HR will see both.
                        </Note>
                      ) : locked ? (
                        <Note tone="success">
                          Submitted {fmtWhen(mine?.submittedAt)}
                          {mine?.correctedAt ? `, corrected ${fmtWhen(mine.correctedAt)}` : ""}.{" "}
                          {viewer.canCorrect
                            ? "You can correct your marks, with a reason, until HR records the decision."
                            : "HR has recorded the decision, so your marks are final."}
                        </Note>
                      ) : (
                        <div style={{ ...muted, display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon name="eye" />
                          The rest of the panel's marks are hidden until you submit yours.
                        </div>
                      )}
                      {interviewStage?.rubricProblem && <Note tone="warning">{interviewStage.rubricProblem}</Note>}
                      {interviewStage?.rubric && (
                        <div style={muted}>
                          Marked on {interviewStage.rubric.name}.
                          {interviewStage.rubric.scale === "levels" && " Choose a level for each criterion; the weighting is done for you."}
                          {interviewStage.rubric.descriptorsFinal === false && " The level wording is provisional until the group's rubric pack arrives."}
                        </div>
                      )}
                      {myCard.length > 0 ? (
                        <div style={stack(8)}>
                          {myCard.map((c, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <label htmlFor={`my-mark-${i}`} style={{ flex: 1, fontSize: "var(--fs-body-sm)", color: "var(--text-1)" }}>{c.label}</label>
                              <RubricMark
                                id={`my-mark-${i}`}
                                row={c}
                                disabled={locked}
                                onChange={(v) => setMyCard((sc) => sc.map((x, xi) => (xi === i ? { ...x, score: v } : x)))}
                              />
                            </div>
                          ))}
                          <div style={{ ...row, borderTop: "1px solid var(--border)", marginTop: 4, paddingTop: 12, fontSize: "var(--fs-body-sm)" }}>
                            <span style={{ fontWeight: 600, color: "var(--text-1)" }}>Total</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, color: "var(--text-1)" }}>
                              {levels ? "Weighted" : `${earned} / ${max}`}
                              <StatusPill tone={pct >= pass ? "success" : "danger"}>{pct}%</StatusPill>
                              <span style={{ ...muted, fontWeight: 400 }}>(pass {pass}%)</span>
                            </span>
                          </div>
                        </div>
                      ) : locked ? (
                        <div style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-1)" }}>Overall mark: <strong>{mine?.score ?? "—"}%</strong></div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <label htmlFor="my-overall-mark" style={{ flex: 1, fontSize: "var(--fs-body-sm)", color: "var(--text-1)" }}>Overall mark (this stage has no scorecard)</label>
                          <span style={{ width: 90 }}>
                            <Input
                              id="my-overall-mark"
                              type="number" value={myOverall}
                              onChange={(e) => setMyOverall(e.target.value === "" ? "" : String(Math.max(0, Math.min(100, Number(e.target.value) || 0))))}
                            />
                          </span>
                          <span style={{ ...muted, width: 40 }}>/ 100</span>
                        </div>
                      )}
                      <Input
                        id="my-observations"
                        multiline
                        rows={3}
                        disabled={locked}
                        placeholder="Your observations (optional)"
                        value={myNotes}
                        onChange={(e) => setMyNotes(e.target.value)}
                      />
                      {correcting && (
                        <>
                          {/* The reason is capped at 500 characters, as the API stores it. */}
                          <Input
                            id="correction-reason"
                            label="Why are you correcting your marks? (required)"
                            multiline
                            rows={2}
                            placeholder="For example: I typed 9 instead of 12 for classroom management"
                            value={reason}
                            onChange={(e) => setReason(e.target.value.slice(0, 500))}
                          />
                          <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <Button fullWidth loading={submitting} icon={<Icon name="check-circle" />} onClick={saveCorrection}>
                                {submitting ? "Saving…" : "Save corrected marks"}
                              </Button>
                            </div>
                            <Button variant="secondary" onClick={cancelCorrection} disabled={submitting}>
                              Cancel
                            </Button>
                          </div>
                        </>
                      )}
                      {locked && viewer.canCorrect && (
                        <Button variant="secondary" fullWidth icon={<Icon name="clipboard-text" />} onClick={() => setCorrecting(true)}>
                          Correct my marks
                        </Button>
                      )}
                      {(mine?.revisions || []).length > 0 && (
                        <div style={{ ...stack(6), borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                          <div style={{ fontSize: "var(--fs-caption)", fontWeight: 500, color: "var(--text-2)" }}>Your earlier marks (kept on record)</div>
                          <RevisionList revisions={mine.revisions} />
                        </div>
                      )}
                      {!viewer.submitted && (
                        <Button fullWidth loading={submitting} icon={<Icon name="paper-plane-right" />} onClick={submitMine}>
                          {submitting ? "Submitting…" : "Submit my marks"}
                        </Button>
                      )}
                    </div>
                  </Section>
                )}

                {/* Panel marks — the API only sends these to HR, or to a panellist who has submitted */}
                {Array.isArray(data?.assessments) && (
                  <Section title="Panel marks">
                    {data.assessments.length === 0 ? (
                      <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>No marks submitted yet.</div>
                    ) : (
                      <div style={stack(16)}>
                        {data.combined && (
                          <div style={{ ...soft, padding: 12 }}>
                            <div style={{ ...row, fontSize: "var(--fs-body-sm)" }}>
                              <span style={{ fontWeight: 600, color: "var(--text-1)" }}>
                                Combined ({data.combined.count} {data.combined.count === 1 ? "mark" : "marks"})
                              </span>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <StatusPill tone={data.combined.passes ? "success" : "danger"}>{data.combined.score}%</StatusPill>
                                <span style={muted}>(pass {data.combined.passingScore}%)</span>
                              </span>
                            </div>
                            {data.combined.parameters.length > 0 && (
                              <div style={{ ...stack(4), marginTop: 8 }}>
                                {data.combined.parameters.map((p) => (
                                  <div key={p.label} style={{ ...row, ...muted, color: "var(--text-2)" }}>
                                    <span>{p.label}</span><span>average {p.average} / {p.maxMarks}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div style={stack(12)}>
                          {data.assessments.map((a, i) => (
                            <div key={i} style={{ paddingBottom: i < data.assessments.length - 1 ? 12 : 0, borderBottom: i < data.assessments.length - 1 ? "1px solid var(--border)" : "none" }}>
                              <div style={{ ...row, fontSize: "var(--fs-body-sm)" }}>
                                <span style={a.attributed ? { fontWeight: 500, color: "var(--text-1)" } : { fontStyle: "italic", color: "var(--text-muted)" }}>{a.byLabel}</span>
                                <span style={{ fontWeight: 600, color: "var(--text-1)" }}>{a.score ?? "—"}%</span>
                              </div>
                              {a.submittedAt && <div style={muted}>{fmtWhen(a.submittedAt)}</div>}
                              {a.enteredBy && <div style={muted}>Paper marks entered by {a.enteredBy.name}</div>}
                              {(a.scorecard || []).length > 0 && (
                                <div style={{ ...muted, color: "var(--text-2)", marginTop: 4 }}>
                                  {a.scorecard.map((c) => `${c.label} ${c.score}/${c.maxMarks}`).join(" · ")}
                                </div>
                              )}
                              {a.notes && <div style={{ ...soft, ...muted, color: "var(--text-2)", marginTop: 4 }}>{a.notes}</div>}
                              {(a.revisions || []).length > 0 && (
                                <div style={{ ...stack(4), marginTop: 4 }}>
                                  <div style={{ fontSize: "var(--fs-caption)", color: "var(--status-warning-text)" }}>Corrected {fmtWhen(a.correctedAt)}. Earlier marks:</div>
                                  <RevisionList revisions={a.revisions} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Section>
                )}

                {/* HR: who is on the panel */}
                {viewer.isHr && (
                  <Section title="Interview panel">
                    <div style={stack(12)}>
                      {(data?.panelMembers || []).length === 0 && (
                        <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>Nobody is on this candidate's panel yet.</div>
                      )}
                      {(data?.panelMembers || []).length > 0 && (
                        <div style={stack(8)}>
                          {(data?.panelMembers || []).map((m) => (
                            <div key={m.userId} style={{ ...row, fontSize: "var(--fs-body-sm)" }}>
                              <span style={{ color: "var(--text-1)" }}>
                                {m.name || "Unnamed user"}
                                {/* Two panellists with one name are told apart by their account. */}
                                {m.email && (data.panelMembers || []).filter((x) => x.name === m.name).length > 1 && <span style={muted}> ({m.email})</span>}
                                {m.enteredBy && <span style={muted}> · paper marks entered by {m.enteredBy}</span>}
                              </span>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                <StatusPill tone={m.status === "submitted" ? "success" : "neutral"}>
                                  {m.status === "submitted" ? "Submitted" : "Not yet submitted"}
                                </StatusPill>
                                {m.status !== "submitted" && (
                                  <Button size="sm" variant="ghost" icon={<Icon name="x" />} onClick={() => removeMember(m.userId)} title="Take off the panel" aria-label="Take off the panel" />
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select
                            value={addWho}
                            placeholder="Add someone to the panel…"
                            onChange={(v) => setAddWho(v)}
                            options={people
                              .filter((p) => !(data?.panelMembers || []).some((m) => m.userId === p.userId))
                              .map((p) => ({ value: p.userId, label: `${p.name} (${p.email})` }))}
                          />
                        </div>
                        <Button variant="secondary" onClick={addMember} disabled={!addWho}>Add</Button>
                      </div>
                    </div>
                  </Section>
                )}

                {/* HR: a panellist's paper mark sheet, typed in for them (HIR-TC1) */}
                {viewer.isHr && !data?.interviewStage?.hrDecision && (
                  <Section title="Enter paper marks">
                    <PaperMarksForm
                      jobId={jobId}
                      stageId={stageId}
                      resumeId={resumeId}
                      criteria={data?.interviewStage?.criteria || []}
                      people={people}
                      onSaved={load}
                    />
                  </Section>
                )}
              </>
            );
          })()}
          {/* HR Notes + Decision — HR only */}
          {data?.viewer?.isHr && (
          <Section title="HR Notes & Decision">
            <div style={stack(16)}>
              <Input
                id="hr-interview-notes"
                label="Interview notes"
                multiline
                rows={6}
                placeholder="Write your observations from the interview…"
                value={notes}
                onChange={e => { setNotes(e.target.value); setSaved(false); }}
              />
              <div style={stack(6)}>
                <div style={{ fontSize: "var(--fs-body-sm)", fontWeight: 500, color: "var(--text-1)" }}>Final decision</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {Object.entries(DECISION_CONFIG).map(([key, cfg]) => (
                    <div key={key} style={{ flex: 1 }}>
                      <Button
                        fullWidth
                        variant={decision === key ? cfg.variant : "secondary"}
                        aria-pressed={decision === key}
                        icon={<Icon name={cfg.icon} />}
                        onClick={() => { setDecision(key); setSaved(false); }}
                      >
                        {cfg.label}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <Button fullWidth loading={saving} icon={<Icon name="check-circle" />} onClick={saveDecision} disabled={!decision}>
                {saving ? "Saving…" : saved ? "Update Decision" : "Save Decision"}
              </Button>
              {saved && decision && (
                <Note tone="success">
                  Decision "{DECISION_CONFIG[decision]?.label}" saved to candidate's record
                </Note>
              )}
            </div>
          </Section>
          )}
        </div>
      </div>
    </Page>
  );
}
